// Signals GTM when it is safe to load non-critical third-party widgets.
//
// Scheduling, per PS's Lighthouse perf brief (Stage 2.1): first interaction or
// idle, whichever comes first, with a hard floor of "not before the load
// event". Nothing here is needed in the first five seconds of a page view.
//
// Widgets injected by the GTM container (Zendesk; Privy before it was paused)
// can't be deferred from this repo, so this pushes a `ps_deferred` dataLayer
// event on that schedule: a GTM tag whose trigger is Custom Event
// `ps_deferred` fires then instead of on page view. Nothing listens to it
// until a tag is switched to that trigger in GTM.
//
// Typebot used to be pre-fetched from here on every page. It no longer is:
// only the two claim pages use it, and components/TypebotWidget.vue loads
// the library itself when it mounts.

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel']

// Idle timeout is a backstop, not a target: requestIdleCallback can otherwise
// wait indefinitely on a page that never goes fully idle.
const IDLE_TIMEOUT_MS = 5000
const FALLBACK_DELAY_MS = 3000

function afterLoadEvent(run) {
  if (document.readyState === 'complete') {
    run()
    return
  }
  window.addEventListener('load', run, { once: true })
}

function onIdleOrInteraction(run) {
  let done = false
  let idleHandle = null
  let timeoutHandle = null

  const listener = () => trigger()

  const cleanup = () => {
    INTERACTION_EVENTS.forEach((event) =>
      window.removeEventListener(event, listener)
    )
    if (
      idleHandle !== null &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(idleHandle)
    }
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle)
    }
  }

  function trigger() {
    if (done) return
    done = true
    cleanup()
    run()
  }

  INTERACTION_EVENTS.forEach((event) =>
    window.addEventListener(event, listener, { passive: true })
  )

  if (typeof window.requestIdleCallback === 'function') {
    idleHandle = window.requestIdleCallback(trigger, {
      timeout: IDLE_TIMEOUT_MS,
    })
  } else {
    timeoutHandle = setTimeout(trigger, FALLBACK_DELAY_MS)
  }
}

function signalGtm() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: 'ps_deferred' })
}

export default () => {
  afterLoadEvent(() => onIdleOrInteraction(signalGtm))
}
